import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CentralProviderSidebar, maestroApiCall, usePermissions } from '@michelin/central-provider';
import { useQuery } from 'react-query';
import { getBEVersion } from 'Types/APITypes';
import { useLocationST } from 'hooks/useLocation';
import {
  Apartment, ArrowBack, SettingsInputAntenna, Store, Warning,
} from '@material-ui/icons';
import { runningOnCanvas } from '@michelin/central-provider/dist/utils/canvas';
import { getVersion } from 'Utils';

interface SidebarProps {
  selectedModule?: string;
}

function isCommercialSection(section: string | undefined) {
  return (section || '').substring(0, 10) === 'commercial';
}

function isRetailSection(section: string | undefined) {
  return ['location', 'service'].includes(section || '');
}

function Sidebar(props: SidebarProps) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { btNumber, stNumber } = useParams<{ btNumber: string; stNumber: string }>();
  const history = useHistory();
  const [commercialMenuOpen, setCommercialMenuOpen] = useState(false);
  const [retailMenuOpen, setRetailMenuOpen] = useState(false);
  const locationQuery = useLocationST(stNumber);
  const beVersionQuery = useQuery(['be-version'], () => maestroApiCall(getBEVersion()), {
    cacheTime: 3600,
    keepPreviousData: true,
  });
  const beVersion = beVersionQuery.data?.version.split('-').pop() || '  -loading-';
  useEffect(() => {
    const commercialOpen = isCommercialSection(props.selectedModule);
    if (commercialOpen) setCommercialMenuOpen(commercialOpen);
    const retailOpen = isRetailSection(props.selectedModule);
    if (retailOpen) setRetailMenuOpen(retailOpen);
  }, [props.selectedModule, setCommercialMenuOpen]);

  function Icon(iconProps: { file: string }) {
    if (!iconProps.file) return null;
    const url = `${process.env.PUBLIC_URL}/static/images/preferencesIcons/${iconProps.file}.svg`;
    return <img src={url} alt={iconProps.file} style={{ width: 26, marginRight: 5, marginLeft: -3 }} />;
  }
  const menuItems: any[] = [];

  if (!locationQuery.isLoading && !locationQuery.error && locationQuery.data) {
    menuItems.push({
      label: t('Locations summary'),
      icon: <ArrowBack color="primary" />,
      onClick: () => history.push(`/${btNumber}`),
      selected: false,
    });
    if (locationQuery.data.lt_location || locationQuery.data.aad_location) {
      menuItems.push({
        label: t('Retail'),
        icon: <Store color="primary" />,
        onClick: () => setRetailMenuOpen(!retailMenuOpen),
        open: retailMenuOpen,
        selected: !retailMenuOpen && isRetailSection(props.selectedModule),
        subitems: [
          {
            label: t('Location Information'),
            path: '/locations',
            icon: <Icon file="locations" />,
            onClick: () => history.push(`/${btNumber}/retail/${stNumber}/location/view`),
            selected: props.selectedModule === 'location',
          },
          {
            label: t('Services'),
            path: '/services',
            icon: <Icon file="sop" />,
            onClick: () => history.push(`/${btNumber}/retail/${stNumber}/services/view`),
            selected: props.selectedModule === 'service',
          },
        ],
      });
    }

    if (
      locationQuery.data.kad_location
      || locationQuery.data.tad_location
      || locationQuery.data.commercial_truck_location
      || locationQuery.data.is_oliver_dealer
    ) {
      const subitems = [
        {
          label: t('Location Information'),
          path: '/commercial',
          icon: <Icon file="locations" />,
          onClick: () => history.push(`/${btNumber}/commercial/${stNumber}/location/view`),
          selected: props.selectedModule === 'commercial',
        },
        {
          label: t('Products'),
          path: '/commercial-products',
          icon: <Icon file="tires" />,
          onClick: () => history.push(`/${btNumber}/commercial/${stNumber}/products/view`),
          selected: props.selectedModule === 'commercial-products',
        },
        ...(permissions.allowsAction('commercial_networks.read')
          ? [
            {
              label: t('Networks'),
              icon: <SettingsInputAntenna color="primary" />,
              onClick: () => history.push(`/${btNumber}/commercial/${stNumber}/networks/view`),
              selected: props.selectedModule === 'commercial-networks',
            },
          ]
          : []),
        {
          label: t('ERS Information'),
          icon: <Icon file="oncall" />,
          onClick: () => history.push(`/${btNumber}/commercial/${stNumber}/ers/view`),
          selected: props.selectedModule === 'commercial-ers',
        },
        {
          label: t('Tire Services'),
          icon: <Icon file="sop" />,
          onClick: () => history.push(`/${btNumber}/commercial/${stNumber}/tire-services/view`),
          selected: props.selectedModule === 'commercial-tire-services',
        },
        {
          label: t('Mechanical Services'),
          icon: <Icon file="sop" />,
          onClick: () => history.push(`/${btNumber}/commercial/${stNumber}/mechanical-services/view`),
          selected: props.selectedModule === 'commercial-mechanical-services',
        },
      ];

      if (permissions.allowsAction('admin_features.update_commercial_overrides')) {
        subitems.push({
          label: t('Overrides'),
          icon: <Warning color="primary" />,
          onClick: () => history.push(`/${btNumber}/commercial/${stNumber}/overrides/view`),
          selected: props.selectedModule === 'commercial-overrides',
        });
      }

      if (!!locationQuery.data.kad_location && !locationQuery.data.commercial_truck_location) {
        const indexToRemove = subitems.findIndex((x) => x.label === 'ERS Information');
        if (indexToRemove !== -1) {
          subitems.splice(indexToRemove, 1);
        }
      }

      menuItems.push({
        label: t('Commercial'),
        icon: <Apartment color="primary" />,
        onClick: () => setCommercialMenuOpen(!commercialMenuOpen),
        selected: !commercialMenuOpen && isCommercialSection(props.selectedModule),
        open: commercialMenuOpen,
        subitems,
      });
    }

    menuItems.push({
      label: t('Contacts'),
      path: '/contacts',
      icon: <Icon file="contacts" />,
      onClick: () => history.push(`/${btNumber}/contacts/${stNumber}/list`),
      selected: props.selectedModule === 'contacts',
    });
    if (
      (locationQuery.data.commercial_truck_location
        || locationQuery.data.kad_location
        || locationQuery.data.tad_location
        || locationQuery.data.is_oliver_dealer
      ) && permissions.allowsAction('technician_service_area.read')
    ) {
      menuItems.push({
        label: t('Technician Service Area'),
        path: '/technician-service-area',
        icon: <Icon file="locations" />,
        onClick: () => history.push(`/${btNumber}/technician-service-area/${stNumber}/list`),
        selected: props.selectedModule === 'technician-service-area',
      });
    }
    if (permissions.allowsAction('change_history.read')) {
      menuItems.push({
        label: t('Change History'),
        path: '/change-history',
        onClick: () => history.push(`/${btNumber}/change-history/${stNumber}/list#entity=location_information`),
        icon: <Icon file="tire-service-manual" />,
        selected: props.selectedModule === 'change-history',
      });
    }

    if (permissions.allowsAction('technician_rotation.read')) {
      menuItems.push({
        label: t('Tech Rotation'),
        path: '/tech-rotation',
        icon: <Icon file="sop" />,
        onClick: () => history.push(`/${btNumber}/tech-rotation/${stNumber}`),
        selected: props.selectedModule === 'tech-rotation',
      });
    }
  }

  return (
    <CentralProviderSidebar
      items={menuItems}
      versions={[
        {
          application: 'UI',
          version: getVersion() || '',
        },
        {
          application: 'API',
          version: beVersion,
        },
      ]}
      showLegalInformation={false}
      showCopyright={!runningOnCanvas()}
      showApplicationSelector={!runningOnCanvas()}
    />
  );
}

export default Sidebar;
